// src/components/shared/NewAppointmentModal.jsx
// Wraps the shared NewAppointment flow inside a modal overlay
import React, { useState, useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { appointmentsService } from 'infrastructure/firebase';
import Calendar from 'shared/components/calendar/Calendar';
import { Avatar, Button, Modal, FormGroup } from 'shared/components/ui';
import { formatCurrency, formatDate } from 'shared/utils/helpers';
import { getAvailableSlots } from 'shared/utils/scheduleLogic';

export default function NewAppointmentModal({ onClose, initialProfessional, initialClient }) {
  const { userProfile, isClient, isProfessional, estabelecimentoId } = useAuth();
  const { professionals, clients, approvedProcedures, appointments, scheduleWindows, showToast, businessConfig } = useApp();

  const [step, setStep]                           = useState(1);
  const [selectedProcedure, setSelectedProcedure] = useState('');
  const [selectedPro, setSelectedPro]             = useState(initialProfessional || (isProfessional ? userProfile?.id : ''));
  const [selectedClient, setSelectedClient]       = useState(initialClient || (isClient ? userProfile?.id : ''));
  const [selectedClientName, setSelectedClientName] = useState(isClient ? userProfile?.name : '');
  const [selectedDate, setSelectedDate]           = useState('');
  const [selectedSlot, setSelectedSlot]           = useState('');
  const [loading, setLoading]                     = useState(false);

  const procedure    = approvedProcedures.find(p => p.id === selectedProcedure) ?? null;
  const professional = professionals.find(p => p.id === selectedPro) ?? null;
  const clientDoc    = clients.find(c => c.id === selectedClient) ?? null;

  const filteredProcedures = approvedProcedures.filter(p => {
    if (!selectedPro) return true;
    const ids = (p.professionals?.length > 0) ? p.professionals : (p.professionalId ? [p.professionalId] : []);
    if (ids.length === 0) return true;
    return ids.includes(selectedPro);
  });

  const availableSlots = useMemo(() => {
    if (!selectedPro || !selectedDate || !procedure) return [];
    return getAvailableSlots({
      date: selectedDate,
      professionalId: selectedPro,
      procedureDuration: procedure.duration || 60,
      appointments,
      windows: scheduleWindows,
      workStart: businessConfig?.workStart || '08:00',
      workEnd: businessConfig?.workEnd || '18:00',
    });
  }, [selectedPro, selectedDate, procedure, appointments, scheduleWindows, businessConfig]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const cEmail = clientDoc?.email || (isClient ? userProfile?.email : '') || '';
      // Cria o agendamento com status 'pending'; a Cloud Function enviará
      // notificação e e-mail de confirmação de presença 1 dia e 5h antes.
      await appointmentsService.add({
        clientId:          selectedClient,
        clientName:        clientDoc?.name || selectedClientName,
        clientEmail:       cEmail,
        professionalId:    selectedPro,
        professionalName:  professional?.name || '',
        procedureId:       selectedProcedure,
        procedureName:     procedure?.name || '',
        date:              selectedDate,
        time:              selectedSlot,
        price:             procedure?.price || 0,
        duration:          procedure?.duration || 60,
        createdBy:         userProfile?.id,
        // Inferir estabelecimentoId do contexto ou do profissional selecionado
        estabelecimentoId: estabelecimentoId || professional?.estabelecimentoId || null,
      });
      showToast('Agendamento realizado! 🎉');
      onClose();
    } catch (err) {
      console.error('[Modal] Erro ao agendar:', err);
      showToast('Erro ao agendar. Tente novamente.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const canNext1 = selectedProcedure && selectedPro && selectedClient;
  const canNext2 = selectedDate && selectedSlot;

  const stepTitles = ['Serviço & Profissional', 'Data & Horário', 'Confirmar'];

  return (
    <Modal open onClose={onClose} size="lg"
      title="Novo Agendamento"
      footer={step === 1
        ? <><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button variant="primary" disabled={!canNext1} onClick={()=>setStep(2)}>Próximo →</Button></>
        : step === 2
        ? <><Button variant="secondary" onClick={()=>setStep(1)}>← Voltar</Button><Button variant="primary" disabled={!canNext2} onClick={()=>setStep(3)}>Próximo →</Button></>
        : <><Button variant="secondary" onClick={()=>setStep(2)}>← Voltar</Button><Button variant="primary" loading={loading} onClick={handleSave}>✓ Confirmar Agendamento</Button></>
      }
    >
      {/* Steps indicator */}
      <div style={{ display:'flex', gap:0, marginBottom:24, background:'#FAF7FB', borderRadius:10, padding:'3px', border:'1px solid #E8E5EC' }}>
        {stepTitles.map((t,i) => (
          <div key={i} style={{
            flex:1, padding:'8px', textAlign:'center', borderRadius:8, fontSize:12, fontWeight:500,
            background: step===i+1 ? '#6B5876' : 'transparent',
            color: step===i+1 ? '#fff' : step>i+1 ? '#6B5876' : '#A8A1B0',
          }}>
            {step > i+1 ? '✓ ' : `${i+1}. `}{t}
          </div>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <>
          {/* Client — admin/pro only */}
          {!isClient && (
            <FormGroup label="Cliente" required>
              <select className="form-select" style={{ width:'100%',padding:'10px 36px 10px 14px',border:'1.5px solid #E8E5EC',borderRadius:12,fontSize:14,fontFamily:"'DM Sans',sans-serif",appearance:'none',backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",backgroundRepeat:'no-repeat',backgroundPosition:'right 12px center',color:'#3D3447',background:'#fff',outline:'none',boxSizing:'border-box' }}
                value={selectedClient} onChange={e => { setSelectedClient(e.target.value); setSelectedClientName(clients.find(c=>c.id===e.target.value)?.name||''); }}>
                <option value="">Selecione o cliente</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name} — {c.phone}</option>)}
              </select>
            </FormGroup>
          )}

          {/* Professional — admin only */}
          {!isProfessional && !isClient && (
            <FormGroup label="Profissional" required>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:8 }}>
                {professionals.map(p => (
                  <div key={p.id} onClick={() => { setSelectedPro(p.id); setSelectedProcedure(''); }}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', border:`2px solid ${selectedPro===p.id?'#6B5876':'#E8E5EC'}`, borderRadius:12, cursor:'pointer', background:selectedPro===p.id?'#E6DEF0':'#fff', transition:'all 0.15s' }}>
                    <Avatar name={p.name} size="sm" />
                    <div>
                      <div style={{ fontWeight:600, fontSize:12 }}>{p.name}</div>
                      <div style={{ fontSize:11, color:'#A8A1B0' }}>{p.specialty}</div>
                    </div>
                  </div>
                ))}
              </div>
            </FormGroup>
          )}

          {/* Procedure */}
          <FormGroup label="Procedimento" required>
            {filteredProcedures.length === 0
              ? <p style={{ color:'#A8A1B0', fontSize:13 }}>{selectedPro ? 'Nenhum procedimento aprovado.' : 'Selecione um profissional primeiro.'}</p>
              : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:8 }}>
                  {filteredProcedures.map(p => (
                    <div key={p.id} onClick={() => setSelectedProcedure(p.id)}
                      style={{ padding:12, border:`2px solid ${selectedProcedure===p.id?'#6B5876':'#E8E5EC'}`, borderRadius:12, cursor:'pointer', background:selectedProcedure===p.id?'#E6DEF0':'#fff', transition:'all 0.15s' }}>
                      <div style={{ fontSize:22, marginBottom:4 }}>{p.emoji||'💆'}</div>
                      <div style={{ fontWeight:600, fontSize:12, marginBottom:2 }}>{p.name}</div>
                      <div style={{ fontSize:11, color:'#A8A1B0', marginBottom:4 }}>{p.duration}min</div>
                      <div style={{ fontWeight:700, color:'#6B5876', fontSize:13 }}>{formatCurrency(p.price)}</div>
                    </div>
                  ))}
                </div>
              )
            }
          </FormGroup>
        </>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <Calendar
          availableSlots={availableSlots}
          selectedDate={selectedDate}
          selectedSlot={selectedSlot}
          onDateSelect={d => { setSelectedDate(d); setSelectedSlot(''); }}
          onSlotSelect={setSelectedSlot}
          workDays={businessConfig?.workDays ?? null}
          workStart={businessConfig?.workStart || '08:00'}
          workEnd={businessConfig?.workEnd || '18:00'}
        />
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <div style={{ background:'#FAF7FB', borderRadius:16, padding:24 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {[
              { l:'Cliente',       v: clientDoc?.name || selectedClientName || userProfile?.name },
              { l:'Profissional',  v: professional?.name || userProfile?.name },
              { l:'Procedimento',  v: procedure?.name },
              { l:'Duração',       v: `${procedure?.duration} min` },
              { l:'Data',          v: formatDate(selectedDate) },
              { l:'Horário',       v: selectedSlot },
              { l:'Valor',         v: formatCurrency(procedure?.price), accent:true },
            ].map(({ l, v, accent }) => (
              <div key={l}>
                <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'#A8A1B0', marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:15, fontWeight:600, color: accent?'#2F5C42':'#3D3447' }}>{v || '—'}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop:20, background:'#eff6ff', border:'1px solid #93c5fd', borderRadius:10, padding:'12px 14px', fontSize:13, color:'#1e40af' }}>
            ℹ️ O cliente receberá uma notificação para confirmar a presença com pelo menos 5h de antecedência.
          </div>
        </div>
      )}
    </Modal>
  );
}
